import { fetcher } from '@/lib/api/fetch'
import axios from '@/lib/api/axios'

/**
 * This contains all of the common context data required in the app.
 */
export const managerProvider = {
  orgsFetched: false,
  billingDetails: null,
  currentUsage: null,
  cache: {},
  getOrganizations: async callback => {
    await callback(managerProvider.organizations)
    managerProvider.orgsFetched = true
  },
  addOrganization: (org, callback) => {
    callback(org)
  },
  removeOrganization: async (id, callback) => {
    await callback()
  },
  addMemberToOrganization: async (id, member, callback) => {
    callback(id, member)
  },
  updateMemberInOrganization: callback => {
    callback()
  },
  removeMemberInOrganization: callback => {
    callback()
  },
  refreshOrganization: async callback => {
    await callback()
  },
  getBilling: async callback => {
    await callback()
  },
  getCache: (key, callback) => {
    return callback(key)
  },
  setCache: (key, value, callback) => {
    callback(key, value)
  },
}

export const setupManagerProvider = (
  setOrganizations,
  setOrgsFetched,
  setBillingDetails,
  setCurrentUsage,
  setManagerCache,
  managerCache,
  authContext,
) => {
  return {
    getBilling: callback => {
      return managerProvider.getBilling(async () => {
        if (!authContext.user?.organization?.id) return

        const { data: usageData } = await axios(
          `/organization/${authContext.user.organization.id}/token-usage-percentage`,
          { method: 'GET' },
          authContext,
          true,
          'billing',
        )

        const { data: billingDetails } = await axios(
          `/organization/${authContext.user.organization.id}`,
          { method: 'GET' },
          authContext,
          true, // Set Content-Type
          'billing', // Use Billing URL
        )

        setBillingDetails(billingDetails)
        setCurrentUsage(usageData)

        callback(managerProvider.billingDetails, managerProvider.currentUsage)
      })
    },
    getOrganizations: callback => {
      return managerProvider.getOrganizations(async orgs => {
        if (orgs && orgs.length > 0) {
          // If we already have orgs then no need to looking for them
          return
        }

        // If we don't have orgs, fetch them
        // Call the API to remove the user
        const res = await fetcher(
          '/api/admin/organizations/' + authContext.user.id,
          { method: 'GET' },
          authContext,
        )

        if (res.status !== 200) {
          console.error('Failed to get organizations for user')
          return
        }

        const data = await res.json()
        setOrganizations(data.organizations || [])
        setOrgsFetched(true)
        callback(data.organizations || [])
      })
    },
    removeOrganization: orgId => {
      return managerProvider.removeOrganization(orgId, async () => {
        setOrganizations(orgs => orgs.filter(org => org.id !== orgId))
      })
    },
    refreshOrganization: orgId => {
      return managerProvider.refreshOrganization(async () => {
        // Update the organization in the database first
        const res = await fetcher(
          '/api/admin/organization/' + orgId,
          { method: 'GET' },
          authContext,
        )

        // Update the organization in the context
        const data = await res.json()
        setOrganizations(orgs => {
          return orgs.map(org => {
            if (org.id === orgId) {
              return data.organization
            }
            return org
          })
        })

        // Update the organization in the user
        authContext.setuser(
          {
            ...authContext.user,
            organization: data.organization,
          },
          () => {},
        )
      })
    },
    addOrganization: org => {
      return managerProvider.addOrganization(org, org => {
        setOrganizations(orgs => [...orgs, org])
      })
    },
    addMemberToOrganization: (id, member, updateUser = true) => {
      return managerProvider.addMemberToOrganization(
        id,
        member,
        (id, member) => {
          setOrganizations(orgs => {
            const updated = orgs.map(org => {
              if (org.id === id) {
                return {
                  ...org,
                  members: [...org.members, member],
                }
              }
              return org
            })

            if (updateUser) {
              authContext.setuser(
                {
                  ...authContext.user,
                  organization: updated.find(org => org.id === id),
                },
                () => {},
              )
            }

            return updated
          })
        },
      )
    },
    updateMemberInOrganization: (id, member, updateUser = true) => {
      return managerProvider.updateMemberInOrganization(() => {
        setOrganizations(orgs => {
          const updated = orgs.map(org => {
            if (org.id === id) {
              org.members = org.members.map(m => {
                if (m.id === member.id) return member
                return m
              })
            }
            return org
          })

          if (updateUser) {
            authContext.setuser(
              {
                ...authContext.user,
                organization: updated.find(org => org.id === id),
              },
              () => {},
            )
          }

          return updated
        })
      })
    },
    removeMemberInOrganization: (id, member, callback, updateUser = true) => {
      return managerProvider.removeMemberInOrganization(() => {
        setOrganizations(orgs => {
          const updated = orgs.map(org => {
            if (org.id === id) {
              org.members = org.members.filter(m => m.id !== member)
            }
            return org
          })

          if (updateUser) {
            authContext.setuser(
              {
                ...authContext.user,
                organization: updated.find(org => org.id === id),
              },
              () => {},
            )
          }

          return updated
        })
        callback()
      })
    },
    getCache: key => {
      return managerProvider.getCache(key, key => {
        return managerCache[key]
      })
    },
    setCache: (key, value) => {
      return managerProvider.setCache(key, value, (key, value) => {
        setManagerCache(cache => ({ ...cache, [key]: value }))
      })
    },
  }
}
